/**
 * LookupFieldSelect Component
 * Enhanced dropdown for lookup fields with search and create new functionality
 */

import React, { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Chip,
  TextField,
  Autocomplete,
  Paper,
  Tooltip,
  createFilterOptions,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { LookupType, LookupValue } from '../types/configRegistryTypes';

// Special value for "Create New" option
const CREATE_NEW_VALUE = '__CREATE_NEW__';

interface LookupOption {
  documentId: string;
  displayName: string;
  code?: string;
  isCreateNew?: boolean;
}

interface LookupFieldSelectProps {
  lookupType: LookupType;
  options: LookupValue[];
  value: string | string[];
  onChange: (value: string | string[]) => void;
  disabled?: boolean;
  onCreateNew?: (lookupTypeCode: string) => void;
  parentValue?: string | null;
  tooltip?: string;
}

// Filter for autocomplete
const filterOptions = createFilterOptions<LookupOption>({
  matchFrom: 'any',
  stringify: (option) => `${option.displayName} ${option.code || ''}`,
});

export const LookupFieldSelect: React.FC<LookupFieldSelectProps> = ({
  lookupType,
  options,
  value,
  onChange,
  disabled = false,
  onCreateNew,
  parentValue,
  tooltip,
}) => {
  const [inputValue, setInputValue] = useState('');

  // Determine if multi-select based on lookupType
  const isMultiSelect = lookupType.allowMultiSelect || (lookupType as any).fieldType === 'multi-select';
  
  // Convert options to autocomplete format and add "Create New" option
  const autocompleteOptions = useMemo((): LookupOption[] => {
    const mappedOptions: LookupOption[] = options.map(opt => ({
      documentId: opt.documentId,
      displayName: opt.displayName || opt.code,
      code: opt.code,
    }));

    // Add "Create New" option at the beginning if callback provided
    if (onCreateNew && !disabled) {
      return [
        {
          documentId: CREATE_NEW_VALUE,
          displayName: `Create New ${lookupType.displayName}`,
          isCreateNew: true,
        },
        ...mappedOptions,
      ];
    }

    return mappedOptions;
  }, [options, lookupType.displayName, onCreateNew, disabled]);

  // Get selected option(s) based on value
  const selectedOptions = useMemo(() => {
    if (isMultiSelect) {
      const values = Array.isArray(value) ? value : [];
      return autocompleteOptions.filter(opt => values.includes(opt.documentId) && !opt.isCreateNew);
    } else {
      const stringValue = Array.isArray(value) ? value[0] : value;
      return autocompleteOptions.find(opt => opt.documentId === stringValue && !opt.isCreateNew) || null;
    }
  }, [value, autocompleteOptions, isMultiSelect]);

  const handleChange = (_event: React.SyntheticEvent, newValue: LookupOption | LookupOption[] | null) => {
    if (!newValue) {
      onChange(isMultiSelect ? [] : '');
      return;
    }

    // Check if "Create New" was clicked
    if (Array.isArray(newValue)) {
      const createNewOption = newValue.find(opt => opt.isCreateNew);
      if (createNewOption) {
        onCreateNew?.(lookupType.code);
        return;
      }
      onChange(newValue.map(opt => opt.documentId));
    } else {
      if (newValue.isCreateNew) {
        onCreateNew?.(lookupType.code);
        return;
      }
      onChange(newValue.documentId);
    }
  };

  // Custom render for options with "Create New" styling
  const renderOption = (props: React.HTMLAttributes<HTMLLIElement>, option: LookupOption) => {
    if (option.isCreateNew) {
      return (
        <li {...props} key={option.documentId}>
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            color: 'primary.main',
            fontWeight: 500,
            width: '100%',
          }}>
            <AddIcon sx={{ mr: 1, fontSize: 18 }} />
            {option.displayName}
          </Box>
        </li>
      );
    }

    return (
      <li {...props} key={option.documentId}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
          <Typography variant="body2">{option.displayName}</Typography>
          {option.code && option.code !== option.displayName && (
            <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
              {option.code}
            </Typography>
          )}
        </Box>
      </li>
    );
  };

  // Custom paper component to add divider after "Create New"
  const CustomPaper = (props: React.HTMLAttributes<HTMLElement>) => (
    <Paper {...props} elevation={8}>
      {props.children}
    </Paper>
  );

  // Render tags for multi-select with improved disabled visibility
  const renderTags = (tagValue: LookupOption[], getTagProps: any) =>
    tagValue.map((option, index) => (
      <Chip
        {...getTagProps({ index })}
        key={option.documentId}
        label={option.displayName}
        size="small"
        sx={{
          maxWidth: 150,
          // Improve visibility in disabled mode - use subtle grey/neutral colors
          '&.Mui-disabled': {
            opacity: 1,
            bgcolor: 'grey.200',
            color: 'text.primary',
            border: '1px solid',
            borderColor: 'grey.400',
            '& .MuiChip-deleteIcon': {
              display: 'none',
            },
          },
        }}
      />
    ));

  return (
    <Box sx={{ width: '100%' }}>
      {/* Label with Single/Multi badge and tooltip */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
        <Typography variant="body2" component="label" sx={{ fontWeight: 500, mr: 1 }}>
          {lookupType.displayName}
          {lookupType.isRequired && <span style={{ color: 'red' }}> *</span>}
        </Typography>
        {tooltip && (
          <Tooltip title={tooltip} arrow placement="top">
            <InfoOutlinedIcon sx={{ fontSize: 16, color: 'text.secondary', cursor: 'help', mr: 1 }} />
          </Tooltip>
        )}
        <Chip
          label={isMultiSelect ? 'Multi' : 'Single'}
          size="small"
          sx={{
            height: 18,
            fontSize: '0.65rem',
            backgroundColor: isMultiSelect ? 'info.light' : 'grey.300',
            color: isMultiSelect ? 'info.contrastText' : 'text.primary',
          }}
        />
      </Box>

      <Autocomplete
        multiple={isMultiSelect}
        options={autocompleteOptions}
        value={selectedOptions as any}
        onChange={handleChange}
        inputValue={inputValue}
        onInputChange={(_event, newInputValue) => setInputValue(newInputValue)}
        getOptionLabel={(option) => option?.displayName || ''}
        isOptionEqualToValue={(option, val) => option.documentId === val.documentId}
        filterOptions={(opts, state) => {
          // Keep "Create New" always visible, filter others
          const createNew = opts.filter(o => o.isCreateNew);
          const regularFiltered = filterOptions(opts.filter(o => !o.isCreateNew), state);
          return [...createNew, ...regularFiltered];
        }}
        renderOption={renderOption}
        renderTags={isMultiSelect ? renderTags : undefined}
        PaperComponent={CustomPaper}
        disabled={disabled}
        size="small"
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder={`Select ${lookupType.displayName}...`}
            size="small"
          />
        )}
        sx={{
          width: '100%',
          // Improve disabled/preview mode visibility
          '& .MuiInputBase-root.Mui-disabled': {
            bgcolor: 'action.hover',
            '& .MuiInputBase-input': {
              color: 'text.primary',
              WebkitTextFillColor: 'unset',
            },
          },
          // Make chips fully visible in disabled mode with subtle styling
          '& .MuiChip-root.Mui-disabled': {
            opacity: 1,
            bgcolor: 'grey.200',
            color: 'text.primary',
            border: '1px solid',
            borderColor: 'grey.400',
          },
          '& .MuiAutocomplete-tag.Mui-disabled': {
            opacity: 1,
          },
        }}
      />
    </Box>
  );
};

export default LookupFieldSelect;

